// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## Identify a single note in scale degrees or solfege
//
// Include description of object & useful info here, please!
import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import SolfegeQuestionGenerator from '../uTheory.Skills/solfegeQuestionGenerator';
import { OneNoteSolfegeDictationInteractivePrototype } from './sharedSolfegeIdMethods';
import type { InteractiveOptions } from './interactivePrototype';
import type { ScaleDegree } from './solfegeDefinitions';

/**
 * oneNoteOptions.skills: array of skills converted internally to level
 */
export interface OneNoteSolfegeIDOptions extends InteractiveOptions {
  /** if true we ignore level/skills and train based
   on what the user has accomplished. */
  levelFromUserData?: boolean;
  level?: number;
  system?: SolfegeSystemName;
  repetition?: number;
  /** default set in solfegeQuestionGenerator */
  tonicizeEvery?: number;
}

export default class OneNoteSolfegeID extends OneNoteSolfegeDictationInteractivePrototype {
  declare options: OneNoteSolfegeIDOptions;

  declare targetSpeedFromSkills: string[];

  constructor(displayDiv: HTMLDivElement, options: OneNoteSolfegeIDOptions = {}, userData?) {
    if (options.testMode) {
      throw new Error('One note solfegeID is not configured to run in checkpoint mode.');
    }
    super(displayDiv, options, userData);
    this.setSolfegeSystem();
    this.targetSpeedFromSkills = OneNoteSolfegeID.registry.skills;
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: OneNoteSolfegeIDOptions, userData?) {
    options.maxQuestions = options.maxQuestions || 24;
    this.generator = new SolfegeQuestionGenerator();

    // We need a level for showing the right buttons, and we should
    // do everything in our power to figure it out!
    if (options.levelFromUserData) options.level = this.getLevel(userData, 'single');
    else if (options.skills && !options.hasOwnProperty('level')) {
      options.level = this.setLevelFromSkill(options);
    } else if (options.hasOwnProperty('level') && !options.skills) {
      options.skills = [this.getSkillFromLevel(options)];
    } else options.level = this.getLevel(userData, 'single');

    this.settings = {
      loadAudioFirst: true,
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a chord progression to establish the key,' +
          ' followed by a note in that key.</p>' +
          '<p>Click the solfege/scale degree of the note you hear.</p>',
        callback: this.startGame,
        thisArg: this,
      },
      instructions: true,
      letters: {
        solfege: { system: this.system, quality: 'Major' },
        customButtons: [
          {
            text: 'Play T<u>o</u>nic',
            value: 'playTonic',
            hotKey: ['o', 'O'],
            variable: 'playTonic',
          },
          { tagName: 'br', perspective: ['tall', 'portrait', 'square'] },
          { text: 'Play <u>N</u>ote', value: 'play', hotKey: ['n', 'N'], variable: 'playNote' },
          { tagName: 'br' },
        ],
      },
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    return super.init(displayDiv, options, userData);
  }

  addListeners() {
    function buttonHandler(input) {
      if (input === 'play') {
        this.play();
      } else if (input === 'playTonic') {
        this.playTonic();
      } else {
        // default:
        this.checkAnswer(input);
      }
    }

    this.letterPanel.addCustomButtonCallback(buttonHandler, this);
  }

  startGame() {
    this.gameStarted = true;
    this.displayInstructions();
    this.letterPanel.solfege.setSolfegeButtons(this.key.quality, this.levelData.degrees);
    this.resize();
  }

  checkAnswer(answer: string) {
    if (this.rightAnswerDelay) return;
    const degree = this.currentQuestion.degree;
    const correct = compareDegrees(degree, answer);
    // const correct =
    //   `${this.currentQuestion.degree}` === `${answer}` ||
    //   (`${this.currentQuestion.degree}` === '8' && Number(answer) === 1);

    if (this.options.testMode) {
      this.checkpointAnswer({
        correct,
        answer,
      });
      return;
    }

    if (correct) {
      this.rightAnswer(250);
    } else {
      this.wrongAnswer();
    }
  }

  gameOver() {
    this.updateInstructions('Nice work!');
    super.gameOver();
  }

  /**
   *  For checkpoint tests, the answer & data from the current question is
   * logged via a callback.
   */
  getAnswerAndData(answerObj: { answer?: ScaleDegree; correct: boolean } = { correct: false }) {
    const studentAnswer = answerObj.answer;
    const correct = answerObj.correct;

    const question = `${this.solfegeSystem.degreeToSolfege(
      this.currentQuestion.degree,
      this.key.quality
    )}`;

    const answerAndData = {
      question,
      questionType: 'OneNoteSolfegeID',
      questionText: `Click the ${this.solfegeSystem} you hear.`,
      questionSVG: null,
      studentAnswerText: studentAnswer
        ? `${this.solfegeSystem.degreeToSolfege(studentAnswer, this.key.quality)}`
        : '__',
      studentAnswerSVG: null,
      correctAnswerText: question,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: [this.currentQuestion.skills],
    };
    return answerAndData;
  }

  static registry = {
    description: 'One-note solfege/scale-degree ID',
    options: {
      levelFromUserData: true,
      tonicizeEvery: 6,
    },
    skills: [
      'sd_123_major',
      'sd_123_minor',
      'sd_12345_major',
      'sd_12345_minor',
      'sd_5678_major',
      'sd_5678_minor',
      'sd_5678_all_minors',
      'sd_1_to_8_major',
      'sd_1_to_8_minor',
      'sd_1_to_8_major_w_octaves',
      'sd_1_to_8_minor_w_octaves',
    ],
  };
}

export function compareDegrees(question: string | number, answer: string | number) {
  // eslint-disable-next-line eqeqeq
  if (question == answer) return true;
  // eslint-disable-next-line eqeqeq
  if (question == '8' && answer == '1') return true;
  // eslint-disable-next-line eqeqeq
  if (answer == '8' && question == '1') return true;
  return false;
}
